// =============================================================================
// web/src/features/quotes/quotePdf.ts
//
// Client-side PDF generation for quotes.
//
//  1. Fetches the full quote (header, sections, lines, joins) from Supabase.
//  2. Builds a pdfmake document definition (cover page, line-item pages, terms).
//  3. Uploads the resulting PDF blob to storage.quote-pdfs/{quote_id}/{n}.pdf.
//  4. Updates quotes.pdf_storage_path / pdf_version / pdf_generated_{at,by}.
//  5. Returns a signed URL (1-hour TTL) for the caller to open in a new tab.
//
// All colour tokens and layout are defined here; template edits happen in this
// file only. When we later move generation server-side (Cloudflare Worker), the
// buildDocDefinition() function moves wholesale and the rest stays in-browser.
// =============================================================================

import { supabase } from "@/lib/supabase";
import { formatQuoteNumber } from "./quoteFormat";

// -----------------------------------------------------------------------------
// Lazy pdfmake loader
//
// pdfmake + vfs_fonts together are ~2.7MB unminified. Statically importing them
// bloats the main JS bundle and pushes it past the PWA service-worker precache
// limit (default 2MB). We load them on-demand the first time a user actually
// clicks "Export PDF" — the dynamic imports generate their own code-split
// chunk, and users who never export don't download them at all.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// pdfmake loader
//
// pdfmake's vfs_fonts.js is a classic browser side-effect script that assumes
// `this === window` so it can write `window.pdfMake.vfs = {...}`. Under ESM
// import, `this` is undefined in strict mode and the script crashes on load.
//
// Fix: use Vite's `?url` suffix to get the asset URLs WITHOUT executing them
// as modules, then inject real <script> tags. This runs them in the context
// pdfmake was built for. Vite bundles the files locally and hashes them for
// caching — fully offline-capable, no CDN dependency.
// -----------------------------------------------------------------------------

// @ts-ignore — Vite's ?url suffix isn't in pdfmake's type declarations
import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Dedupe if this script has already been injected.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-pdfmake-tag="${tag}"]`
    );
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
      } else {
        existing.addEventListener("load",  () => resolve());
        existing.addEventListener("error", () => reject(new Error(`Failed to (re)load ${tag}`)));
      }
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.pdfmakeTag = tag;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${tag} from ${src}`));
    document.head.appendChild(script);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) {
    return existing;
  }

  console.log("[quotePdf] Injecting pdfmake script tags...");
  await loadScriptOnce(pdfmakeScriptUrl,  "pdfmake-core");
  console.log("[quotePdf] pdfmake.min.js loaded");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  console.log("[quotePdf] vfs_fonts.js loaded");

  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) {
    throw new Error("window.pdfMake is missing after script load — unexpected pdfmake build");
  }
  const vfsKeys = pdfMake.vfs ? Object.keys(pdfMake.vfs) : [];
  console.log("[quotePdf] pdfmake ready. vfs has", vfsKeys.length, "files",
    vfsKeys.length > 0 ? `(e.g. ${vfsKeys.slice(0, 3).join(", ")})` : "");

  if (vfsKeys.length === 0) {
    throw new Error("pdfmake vfs is empty — fonts will fail. Check pdfmake package version.");
  }
  return pdfMake;
}

// -----------------------------------------------------------------------------
// Brand tokens (match the app's Tailwind tokens)
// -----------------------------------------------------------------------------

const COLOR = {
  primary:     "#ff7844",   // Coretel brand orange
  primaryWash: "#fff4ee",   // Soft peach for panels
  primarySoft: "#ffd9c7",   // Medium peach for mid panels
  ink:         "#111111",
  text:        "#1a1a1a",
  muted:       "#666666",
  dim:         "#999999",
  rule:        "#e5e5e5",
  ruleDark:    "#cccccc",
  rowAlt:      "#fafafa",
  subtotalBg:  "#f4f4f4",
};

const COMPANY = {
  name: "Coretel Group Pty Ltd",
  abn:  "63 690 407 679",
  address: "60 Lisson Grove, Wooloowin QLD 4030",
  phone: "+61 423 661 366",
  email: "accounts@coretelgroup.com",
  web:   "coretelgroup.com",
};

// -----------------------------------------------------------------------------
// Types (thin interfaces — we query shape-specifically and trust the DB)
// -----------------------------------------------------------------------------

interface QuoteLineRow {
  line_number: number;
  description: string | null;
  description_secondary: string | null;
  quantity: number;
  unit: string;
  cost_price_ex_gst: number | null;
  sell_price_ex_gst: number;
  line_total_ex_gst: number;
  qty_people: number | null;
  qty_hours: number | null;
  qty_days: number | null;
  sort_order: number | null;
  rate_line: {
    line_code: string | null;
    display_name: string | null;
  } | null;
}

interface QuoteSectionRow {
  section_id: string;
  section_name: string;
  section_description: string | null;
  sort_order: number;
  quote_lines: QuoteLineRow[];
}

interface QuoteRow {
  quote_id: string;
  quote_number: string;
  quote_date: string;
  valid_until: string | null;
  revision_number: number | null;
  status: string | null;
  scope_bullets_text: string | null;
  assumptions_exclusions_text: string | null;
  notes_text: string | null;
  terms_text: string | null;
  subtotal_excl_gst: number;
  gst_amount: number;
  total_incl_gst: number;
  pdf_version: number;
  is_simplified_pdf: boolean | null;
  // Client / site / bid joined via `*` because their schemas have evolved and
  // we don't want to chase missing-column errors. Code defensively reads
  // common field name variants below.
  client: any;
  site:   any;
  bid:    any;
  quote_sections: QuoteSectionRow[];
  // mig 455 — typed site / client details; mig 454 — pricing basis
  site_name_text?: string | null;
  site_address_text?: string | null;
  client_reference?: string | null;
  client_contact_text?: string | null;
  pricing_basis?: string | null;
}

// -----------------------------------------------------------------------------
// Formatters
// -----------------------------------------------------------------------------

const moneyFmt = new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function fmtMoney(n: number | null | undefined): string {
  return moneyFmt.format(Number(n ?? 0));
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

const UNIT_LABELS: Record<string, string> = {
  hour: "hr", day: "day", week: "wk", night: "night",
  kilometre: "km", linear_metre: "lm",
  cubic_metre: "m³", square_metre: "m²",
  kilogram: "kg", tonne: "t",
  each: "ea", lump_sum: "LS", litre: "L",
};

function unitLabel(u: string | null | undefined): string {
  if (!u) return "";
  return UNIT_LABELS[u] ?? u.replace(/_/g, " ");
}

// Builds the human-readable breakdown line: "3 crew × 12 hrs × 1 day = 36 hrs"
function breakdownDescription(l: QuoteLineRow): string | null {
  const p = Number(l.qty_people || 0);
  const h = Number(l.qty_hours || 0);
  const d = Number(l.qty_days || 0);
  if (l.unit === "hour") {
    if (p > 0 && h > 0 && d > 0) return `${p} crew × ${h} hrs × ${d} day${d === 1 ? "" : "s"}`;
    if (p > 0 && h > 0)          return `${p} crew × ${h} hrs`;
    if (h > 0 && d > 0)          return `${h} hrs × ${d} day${d === 1 ? "" : "s"}`;
    return null;
  }
  if (l.unit === "night" && p > 0) {
    const n = h > 0 ? h : Number(l.quantity || 0) / (p || 1);
    return `${p} × ${Math.round(n)} night${n === 1 ? "" : "s"}`;
  }
  if (l.unit === "day" && p > 0 && d > 0) {
    return `${p} crew × ${d} day${d === 1 ? "" : "s"}`;
  }
  return null;
}

// -----------------------------------------------------------------------------
// Logo loader: fetches /coretel-logo-full.png from the app's public/ and
// converts it to a base64 data URL pdfmake can embed.
// -----------------------------------------------------------------------------

async function fetchLogoDataUrl(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result as string);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Full-page doc definition
// -----------------------------------------------------------------------------

function buildDocDefinition(
  q: QuoteRow,
  opts: { logoDataUrl: string | null; preparedByName: string; preparedByEmail: string }
): any {
  // Defensive field reads — schemas evolve, so try every plausible name.
  // Real columns: clients.trading_name / legal_name, bids.bid_title, etc.
  // mig 468 — the client as typed on the quote wins (a "Direct — other clients" job)
  const clientName = (q as any).client_name_text?.trim() || (
    q.client?.trading_name ??
    q.client?.legal_name ??
    q.client?.company_name ??
    q.client?.client_name ??
    q.client?.display_name ??
    q.client?.name ??
    q.client?.title ??
    "—");

  // mig 455 — a standalone quote with no site record prints the typed site
  const siteName = q.site?.site_name ?? q.site?.name ?? q.site?.title ?? q.site_name_text ?? null;
  const siteCode = q.site?.site_code ?? q.site?.code ?? null;
  const siteLabel = siteName
    ? `${siteName}${siteCode ? ` (${siteCode})` : ""}${!q.site && q.site_address_text ? ` — ${q.site_address_text}` : ""}`
    : (siteCode ?? "—");

  const bidNumber = q.bid?.bid_number ?? q.bid?.number ?? q.bid?.bid_code ?? null;
  const bidTitle  = q.bid?.bid_title ?? q.bid?.title ?? q.bid?.name ?? q.bid?.display_name ?? null;

  const validUntil = q.valid_until ?? addDaysISO(q.quote_date, 30);
  const revNumber  = (q.revision_number ?? 1);
  const nextVersion = (q.pdf_version ?? 0) + 1;
  const projectTitle = bidTitle ?? siteName ?? "Project quote";
  // T-244 — display number with R-suffix when this is a revision (e.g. Q-26-0042-R1).
  // Used everywhere the human-readable quote number appears in the PDF.
  const displayNumber = formatQuoteNumber(q);

  // Sort sections + lines in display order. Schema has sort_order on sections
  // (no separate section_number) — we derive the display index 01, 02, 03...
  // from the sorted position so it's always contiguous regardless of gaps.
  const sections = [...(q.quote_sections ?? [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((s, idx) => ({
      ...s,
      display_number: idx + 1,
      // Order by the builder's drag-order (sort_order), NOT line_number.
      // line_number is the stable id stamped at creation (3,4,5,7,9…); the
      // manager arranges the "story" via drag which writes sort_order. The PDF
      // must mirror that. Fall back to line_number only if sort_order is unset.
      quote_lines: [...(s.quote_lines ?? [])].sort(
        (a, b) => (a.sort_order ?? a.line_number ?? 0) - (b.sort_order ?? b.line_number ?? 0)
      ),
      subtotal: (s.quote_lines ?? []).reduce(
        (sum, l) => sum + Number(l.line_total_ex_gst ?? 0), 0
      ),
    }));

  // ---------------------------------------------------------------------------
  // Cover page
  // ---------------------------------------------------------------------------

  const coverHeader: any = {
    columns: [
      opts.logoDataUrl
        ? { image: opts.logoDataUrl, width: 140, alignment: "left" }
        : {
            text: "CORETEL",
            fontSize: 22, bold: true, color: COLOR.primary,
            margin: [0, 12, 0, 0],
          },
      {
        width: "auto",
        table: {
          body: [[
            {
              text: "QUOTE",
              fontSize: 18, bold: true, color: COLOR.primary,
              fillColor: COLOR.primaryWash,
              margin: [16, 8, 16, 8],
              characterSpacing: 2,
            } as any,
          ]],
        },
        layout: "noBorders",
        alignment: "right",
      },
    ],
    columnGap: 10,
  };

  const quoteHeadline: any = {
    stack: [
      { text: displayNumber, fontSize: 28, bold: true, color: COLOR.ink, margin: [0, 24, 0, 0] },
      { text: projectTitle, fontSize: 13, color: COLOR.muted, margin: [0, 2, 0, 0] },
      { canvas: [
        { type: "line", x1: 0, y1: 0, x2: 80, y2: 0, lineWidth: 2, lineColor: COLOR.primary },
      ], margin: [0, 10, 0, 0] },
    ],
  };

  const infoCard = (title: string, rows: Array<[string, string]>): any => ({
    table: {
      widths: [70, "*"],
      body: [
        [
          {
            text: title.toUpperCase(),
            fontSize: 8, bold: true, color: COLOR.primary,
            colSpan: 2,
            margin: [0, 0, 0, 6],
            characterSpacing: 1.2,
          },
          {},
        ],
        ...rows.map(([k, v]) => [
          { text: k, fontSize: 9, color: COLOR.muted, margin: [0, 2, 0, 2] },
          { text: v || "—", fontSize: 9, color: COLOR.text, margin: [0, 2, 0, 2] },
        ]),
      ],
    },
    layout: "noBorders",
  });

  const preparedForRows: Array<[string, string]> = [
    ["Client",      clientName],
    ["Site",        siteLabel],
    ...(q.client_contact_text ? [["Attention", q.client_contact_text] as [string, string]] : []),
    ...(q.client_reference ? [["Your ref", q.client_reference] as [string, string]] : []),
    ...(bidNumber ? [["BID ref", bidNumber] as [string, string]] : []),
  ];

  const preparedByRows: Array<[string, string]> = [
    ["Issued",      fmtDate(q.quote_date)],
    ["Valid until", fmtDate(validUntil)],
    ["Revision",    `${revNumber}`],
    ["Contact",     opts.preparedByName],
    ["Email",       opts.preparedByEmail],
    ["Phone",       COMPANY.phone],
  ];

  const partiesBlock: any = {
    columns: [
      { width: "*", ...infoCard("Prepared for", preparedForRows) },
      { width: 20, text: "" },
      { width: "*", ...infoCard("Prepared by Coretel Group", preparedByRows) },
    ],
    margin: [0, 30, 0, 0],
  };

  // Scope snapshot (section names + subtotals)
  const scopeSnapshot: any = {
    margin: [0, 30, 0, 0],
    stack: [
      { text: "SCOPE SUMMARY", fontSize: 8, bold: true, color: COLOR.primary, characterSpacing: 1.2, margin: [0, 0, 0, 6] },
      {
        table: {
          widths: [28, "*", 80],
          headerRows: 1,
          body: [
            [
              { text: "#",              fontSize: 8, bold: true, color: COLOR.muted, border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
              { text: "Scope section",  fontSize: 8, bold: true, color: COLOR.muted, border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
              { text: "Amount Ex GST",  fontSize: 8, bold: true, color: COLOR.muted, alignment: "right", border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
            ],
            ...sections.map((s) => [
              { text: String(s.display_number).padStart(2, "0"), fontSize: 10, color: COLOR.muted, margin: [0, 4, 0, 4], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.rule] },
              { text: s.section_name,                             fontSize: 10, color: COLOR.text,  margin: [0, 4, 0, 4], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.rule] },
              { text: fmtMoney(s.subtotal),                        fontSize: 10, color: COLOR.text,  margin: [0, 4, 0, 4], alignment: "right", border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.rule] },
            ]),
            ...(sections.length === 0 ? [[
              { text: "—", fontSize: 9, color: COLOR.muted, colSpan: 3, alignment: "center", margin: [0, 10, 0, 10] },
              {}, {},
            ]] : []),
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0, paddingRight: () => 0,
          paddingTop: () => 0,  paddingBottom: () => 0,
        },
      },
    ],
  };

  // T-203 — Scope text block (page 1, only when scope_bullets_text is set).
  // Mirrors the termsSection visual pattern but sits inline on the cover page.
  // When this renders, the scope summary + totals get pushed to page 2 (see
  // summaryAndTotals below) so page 1 stays clean.
  const hasScopeText = !!(q.scope_bullets_text?.trim());
  const scopeTextBlock: any = hasScopeText ? {
    margin: [0, 30, 0, 0],
    stack: [
      { text: "SCOPE", fontSize: 8, bold: true, color: COLOR.primary, characterSpacing: 1.2, margin: [0, 0, 0, 6] },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: COLOR.rule }],
        margin: [0, 0, 0, 10],
      },
      { text: q.scope_bullets_text!.trim(), fontSize: 10, color: COLOR.text, lineHeight: 1.4 },
    ],
  } : null;

  // Big totals panel (right-aligned, brand-wash background)
  const totalsPanel: any = {
    margin: [0, 20, 0, 0],
    columns: [
      { width: "*", text: "" },
      {
        width: 290,
        table: {
          widths: ["*", 140],
          body: [
            [
              { text: "Subtotal Ex GST", fontSize: 10, color: COLOR.muted, margin: [12, 8, 8, 4], border: [false, false, false, false] },
              { text: fmtMoney(q.subtotal_excl_gst), fontSize: 11, color: COLOR.text, alignment: "right", margin: [8, 8, 12, 4], border: [false, false, false, false] },
            ],
            [
              { text: "GST (10%)", fontSize: 10, color: COLOR.muted, margin: [12, 4, 8, 8], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
              { text: fmtMoney(q.gst_amount), fontSize: 11, color: COLOR.text, alignment: "right", margin: [8, 4, 12, 8], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
            ],
            [
              { text: "TOTAL INC GST", fontSize: 11, bold: true, color: COLOR.primary, margin: [12, 10, 8, 10], fillColor: COLOR.primaryWash, border: [false, false, false, false], characterSpacing: 1.2 },
              { text: fmtMoney(q.total_incl_gst), fontSize: 14, bold: true, color: COLOR.primary, alignment: "right", margin: [8, 10, 12, 10], fillColor: COLOR.primaryWash, border: [false, false, false, false] },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0 : 0),
          vLineWidth: () => 0,
        },
      },
    ],
  };

  // ---------------------------------------------------------------------------
  // Line item pages — one per section
  // ---------------------------------------------------------------------------

  const sectionBlocks: any[] = sections.map((s, idx) => {
    const headerBar: any = {
      table: {
        widths: [30, "*", 90],
        body: [[
          { text: String(s.display_number).padStart(2, "0"), fontSize: 12, bold: true, color: COLOR.primary, fillColor: COLOR.primaryWash, margin: [10, 8, 6, 8], border: [false, false, false, false] },
          { text: s.section_name,                             fontSize: 12, bold: true, color: COLOR.ink,     fillColor: COLOR.primaryWash, margin: [0, 8, 0, 8],  border: [false, false, false, false] },
          { text: fmtMoney(s.subtotal),                       fontSize: 12, bold: true, color: COLOR.ink,     fillColor: COLOR.primaryWash, margin: [0, 8, 10, 8], alignment: "right", border: [false, false, false, false] },
        ]],
      },
      layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
      margin: [0, idx === 0 ? 0 : 18, 0, 0],
    };

    // Line items table. When the quote is flagged "simple", we hide the Qty,
    // Unit, and Rate Ex GST columns so the client cannot derive our rate from
    // line_total / qty. Every line item row still renders; only those three
    // columns drop out.
    const isSimple = !!q.is_simplified_pdf;

    const headerRow = [
      { text: "#",              fontSize: 8, bold: true, color: "#ffffff", fillColor: COLOR.primary, margin: [6, 6, 6, 6] },
      { text: "Item",           fontSize: 8, bold: true, color: "#ffffff", fillColor: COLOR.primary, margin: [6, 6, 6, 6] },
      ...(isSimple ? [] : [{ text: "Qty",            fontSize: 8, bold: true, color: "#ffffff", fillColor: COLOR.primary, margin: [6, 6, 6, 6], alignment: "right" }]),
      ...(isSimple ? [] : [{ text: "Unit",           fontSize: 8, bold: true, color: "#ffffff", fillColor: COLOR.primary, margin: [6, 6, 6, 6] }]),
      ...(isSimple ? [] : [{ text: "Rate Ex GST",    fontSize: 8, bold: true, color: "#ffffff", fillColor: COLOR.primary, margin: [6, 6, 6, 6], alignment: "right" }]),
      { text: "Amount Ex GST",  fontSize: 8, bold: true, color: "#ffffff", fillColor: COLOR.primary, margin: [6, 6, 6, 6], alignment: "right" },
    ];

    const linesBody = s.quote_lines.map((l, i) => {
      // The manager's typed description is the item TITLE — it REPLACES the
      // rate-line name (e.g. "Rigger") instead of nesting under it, so a line
      // reads as one heading + optional detail, not "Rigger" then the same text
      // on a second line. Falls back to the rate-line name when no custom text.
      const itemName = (l.description && l.description.trim())
        ? l.description.trim()
        : (l.rate_line?.display_name ?? "—");
      const lineCode = l.rate_line?.line_code ?? null;
      const breakdown = breakdownDescription(l);
      // Optional secondary detail line (extra context beneath the title).
      const descriptionSecondary = l.description_secondary?.trim() || null;

      const itemStack: any[] = [];
      if (lineCode) {
        itemStack.push({
          text: [
            { text: lineCode, fontSize: 7, color: COLOR.primary, bold: true, characterSpacing: 0.8 },
            { text: "   " + itemName, fontSize: 10, color: COLOR.ink, bold: true },
          ],
        });
      } else {
        itemStack.push({ text: itemName, fontSize: 10, color: COLOR.ink, bold: true });
      }
      if (breakdown) {
        itemStack.push({ text: breakdown, fontSize: 8, color: COLOR.muted, italics: true, margin: [0, 2, 0, 0] });
      }
      if (descriptionSecondary) {
        itemStack.push({ text: descriptionSecondary, fontSize: 9, color: COLOR.muted, margin: [0, 2, 0, 0] });
      }

      const altBg = i % 2 === 1 ? COLOR.rowAlt : undefined;
      const cellProps = { fillColor: altBg, margin: [6, 6, 6, 6] };

      return [
        { text: String(i + 1), fontSize: 9, color: COLOR.muted, alignment: "right", ...cellProps },
        { stack: itemStack, ...cellProps },
        ...(isSimple ? [] : [{ text: Number(l.quantity).toLocaleString("en-AU", { maximumFractionDigits: 2 }), fontSize: 10, color: COLOR.text, alignment: "right", ...cellProps }]),
        ...(isSimple ? [] : [{ text: unitLabel(l.unit), fontSize: 9, color: COLOR.muted, ...cellProps }]),
        ...(isSimple ? [] : [{ text: fmtMoney(l.sell_price_ex_gst), fontSize: 10, color: COLOR.text, alignment: "right", ...cellProps }]),
        { text: fmtMoney(l.line_total_ex_gst), fontSize: 10, bold: true, color: COLOR.ink, alignment: "right", ...cellProps },
      ];
    });

    const subtotalRow = isSimple
      ? [
          { text: "",                          fillColor: COLOR.subtotalBg, border: [false, true, false, false], borderColor: [COLOR.ruleDark, "transparent", "transparent", "transparent"] },
          { text: `Section ${String(s.display_number).padStart(2, "0")} subtotal`, fontSize: 9, bold: true, color: COLOR.ink, alignment: "right", colSpan: 1, fillColor: COLOR.subtotalBg, margin: [6, 6, 6, 6], border: [false, true, false, false], borderColor: [COLOR.ruleDark, "transparent", "transparent", "transparent"] } as any,
          { text: fmtMoney(s.subtotal), fontSize: 10, bold: true, color: COLOR.ink, alignment: "right", fillColor: COLOR.subtotalBg, margin: [6, 6, 6, 6], border: [false, true, false, false], borderColor: [COLOR.ruleDark, "transparent", "transparent", "transparent"] },
        ]
      : [
          { text: "",                          fillColor: COLOR.subtotalBg, border: [false, true, false, false], borderColor: [COLOR.ruleDark, "transparent", "transparent", "transparent"] },
          { text: `Section ${String(s.display_number).padStart(2, "0")} subtotal`, fontSize: 9, bold: true, color: COLOR.ink, alignment: "right", colSpan: 4, fillColor: COLOR.subtotalBg, margin: [6, 6, 6, 6], border: [false, true, false, false], borderColor: [COLOR.ruleDark, "transparent", "transparent", "transparent"] } as any,
          {}, {}, {},
          { text: fmtMoney(s.subtotal), fontSize: 10, bold: true, color: COLOR.ink, alignment: "right", fillColor: COLOR.subtotalBg, margin: [6, 6, 6, 6], border: [false, true, false, false], borderColor: [COLOR.ruleDark, "transparent", "transparent", "transparent"] },
        ];

    const linesTable: any = {
      table: {
        headerRows: 1,
        widths: isSimple ? [22, "*", 70] : [22, "*", 40, 35, 60, 70],
        dontBreakRows: true,
        body: [headerRow, ...linesBody, subtotalRow],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 0, paddingRight: () => 0,
        paddingTop: () => 0,  paddingBottom: () => 0,
      },
      margin: [0, 4, 0, 0],
    };

    // Keep section header + table together where possible
    return [headerBar, linesTable];
  });

  // ---------------------------------------------------------------------------
  // Totals + footer pages (terms, exclusions, notes)
  // ---------------------------------------------------------------------------

  const grandTotals: any = {
    margin: [0, 30, 0, 0],
    pageBreak: "before",
    columns: [
      { width: "*", text: "" },
      {
        width: 320,
        table: {
          widths: ["*", 150],
          body: [
            [
              { text: "Subtotal Ex GST", fontSize: 10, color: COLOR.muted, margin: [12, 10, 8, 4], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.rule] },
              { text: fmtMoney(q.subtotal_excl_gst), fontSize: 11, color: COLOR.text, alignment: "right", margin: [8, 10, 12, 4], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.rule] },
            ],
            [
              { text: "GST (10%)", fontSize: 10, color: COLOR.muted, margin: [12, 6, 8, 6], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
              { text: fmtMoney(q.gst_amount), fontSize: 11, color: COLOR.text, alignment: "right", margin: [8, 6, 12, 6], border: [false, false, false, true], borderColor: ["transparent", "transparent", "transparent", COLOR.ruleDark] },
            ],
            [
              { text: "TOTAL INC GST", fontSize: 12, bold: true, color: COLOR.primary, fillColor: COLOR.primaryWash, margin: [12, 12, 8, 12], border: [false, false, false, false], characterSpacing: 1.2 },
              { text: fmtMoney(q.total_incl_gst), fontSize: 16, bold: true, color: COLOR.primary, fillColor: COLOR.primaryWash, alignment: "right", margin: [8, 12, 12, 12], border: [false, false, false, false] },
            ],
          ],
        },
        layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
      },
    ],
  };

  function termsSection(title: string, body: string | null | undefined): any[] {
    const trimmed = (body ?? "").trim();
    if (!trimmed) return [];
    return [
      {
        text: title.toUpperCase(),
        fontSize: 9, bold: true, color: COLOR.primary, characterSpacing: 1.2,
        margin: [0, 24, 0, 6],
      },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: COLOR.rule }],
        margin: [0, 0, 0, 10],
      },
      { text: trimmed, fontSize: 10, color: COLOR.text, lineHeight: 1.4 },
    ];
  }

  const termsBlock: any[] = [
    // T-203 — when scope text is shown on page 1 (hasScopeText), skip it here
    // to avoid duplication. Assumptions/Notes/T&Cs still belong at the back.
    ...(hasScopeText ? [] : termsSection("Scope", q.scope_bullets_text)),
    ...termsSection("Assumptions & Exclusions", q.assumptions_exclusions_text),
    ...termsSection("Notes",                    q.notes_text),
    ...termsSection("Terms & Conditions",       q.terms_text),
  ];

  // Signature block
  const signatureBlock: any = {
    margin: [0, 40, 0, 0],
    table: {
      widths: ["*", 20, "*"],
      body: [[
        {
          stack: [
            { text: "AUTHORISED BY CORETEL GROUP", fontSize: 8, bold: true, color: COLOR.primary, characterSpacing: 1.2, margin: [0, 0, 0, 16] },
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: 220, y2: 0, lineWidth: 0.5, lineColor: COLOR.ink }] },
            { text: opts.preparedByName, fontSize: 10, color: COLOR.text, margin: [0, 4, 0, 0] },
            { text: "Director, Coretel Group Pty Ltd", fontSize: 9, color: COLOR.muted },
            { text: fmtDate(q.quote_date), fontSize: 9, color: COLOR.muted, margin: [0, 2, 0, 0] },
          ],
          border: [false, false, false, false],
        },
        { text: "", border: [false, false, false, false] },
        {
          stack: [
            { text: "ACCEPTED BY CLIENT", fontSize: 8, bold: true, color: COLOR.primary, characterSpacing: 1.2, margin: [0, 0, 0, 16] },
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: 220, y2: 0, lineWidth: 0.5, lineColor: COLOR.ink }] },
            { text: "Signature", fontSize: 9, color: COLOR.muted, margin: [0, 4, 0, 8] },
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: 220, y2: 0, lineWidth: 0.5, lineColor: COLOR.ink }] },
            { text: "Name / Date / PO Number", fontSize: 9, color: COLOR.muted, margin: [0, 4, 0, 0] },
          ],
          border: [false, false, false, false],
        },
      ]],
    },
    layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
  };

  // ---------------------------------------------------------------------------
  // Assemble doc
  // ---------------------------------------------------------------------------

  return {
    info: {
      title:    `${displayNumber} — ${projectTitle}`,
      author:   COMPANY.name,
      subject:  `Quote for ${clientName}`,
      keywords: `quote, coretel, ${clientName}`,
      creator:  COMPANY.name,
    },
    pageSize: "A4",
    pageMargins: [40, 85, 40, 55],

    // Draft quotes carry a subtle DRAFT watermark so a preliminary can be shown
    // to the client (while waiting on the formal RFQ) without looking final.
    // Marking the quote "sent" clears it — that's the clean client copy.
    ...(q.status === "draft"
      ? { watermark: { text: "DRAFT", color: COLOR.primary, opacity: 0.06, bold: true, italics: false } }
      : {}),

    defaultStyle: {
      font:  "Roboto",
      fontSize: 10,
      color: COLOR.text,
      lineHeight: 1.2,
    },

    // -- Top header strip (pages 2+ only, cover page has its own header) --
    header: (currentPage: number) => {
      if (currentPage === 1) return null;
      return {
        margin: [40, 25, 40, 0],
        columns: [
          opts.logoDataUrl
            ? { image: opts.logoDataUrl, width: 70, alignment: "left" }
            : { text: "CORETEL", fontSize: 11, bold: true, color: COLOR.primary },
          {
            text: [
              { text: displayNumber, bold: true, color: COLOR.ink, fontSize: 10 },
              { text: `   ·   ${clientName}`, color: COLOR.muted, fontSize: 10 },
              { text: `   ·   ${projectTitle}`, color: COLOR.muted, fontSize: 10 },
            ],
            alignment: "right",
            margin: [0, 6, 0, 0],
          },
        ],
      };
    },

    // -- Bottom footer (every page) --
    footer: (currentPage: number, pageCount: number) => ({
      margin: [40, 0, 40, 20],
      columns: [
        {
          text: `${COMPANY.name}  ·  ABN ${COMPANY.abn}`,
          fontSize: 8, color: COLOR.muted, alignment: "left",
        },
        {
          // T-244 — displayNumber already includes the R-suffix, so we drop the redundant "Rev N".
          // pdf_version (v{nextVersion}) is the PDF regen counter — distinct from revision_number — and stays.
          text: `${displayNumber}  ·  v${nextVersion}  ·  Issued ${fmtDate(q.quote_date)}`,
          fontSize: 8, color: COLOR.muted, alignment: "center",
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 8, color: COLOR.muted, alignment: "right",
        },
      ],
    }),

    content: [
      // T-203 — Page 1 cover.
      // If scope_bullets_text is set: header + parties + scope text on p1,
      //   summary + totals forced to p2 (kept together via unbreakable stack).
      // If no scope text: header + parties + summary + totals all on p1
      //   (unbreakable stack still prevents the totals splitting across pages).
      coverHeader,
      quoteHeadline,
      partiesBlock,
      ...(scopeTextBlock ? [scopeTextBlock] : []),
      {
        ...(hasScopeText ? { pageBreak: "before" } : {}),
        unbreakable: true,
        stack: [scopeSnapshot, totalsPanel],
      },

      // Pages 2+ — line items
      { text: "", pageBreak: "after" },
      {
        text: "DETAILED LINE ITEMS",
        fontSize: 9, bold: true, color: COLOR.primary, characterSpacing: 1.2,
        margin: [0, 0, 0, 8],
      },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: COLOR.rule }],
        margin: [0, 0, 0, 14],
      },
      ...sectionBlocks.flat(),

      // Final page — totals + terms + signature
      grandTotals,
      ...termsBlock,
      signatureBlock,
    ],
  };
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export interface GenerateQuotePdfResult {
  signedUrl: string;
  storagePath: string;
  version: number;
}

/**
 * Generates a PDF for the given quote, uploads it to Supabase Storage,
 * updates the quotes row with path / version / timestamp / user, and
 * returns a 1-hour signed URL.
 *
 * Caller typically opens result.signedUrl in a new tab immediately.
 */
/**
 * mig 454/455 SoR quotes — what goes in the PDF (James, 27 Oct 2026: "we do
 * not quote SoRs in a PDF, it would be in an excel template they provide us
 * like the pricebook and PSUMs are quotes, and we may send them in batches or
 * individually or all together"). Default when omitted: everything.
 */
export interface SorPdfSelection {
  includeSor: boolean;          // the schedule-of-rates lines under the client's headings
  psumItemIds: string[] | null; // null = every PSUM item; [] = none; else just these
}

export async function generateQuotePdf(
  quoteId: string,
  preparedBy: { name: string; email: string },
  selection?: SorPdfSelection
): Promise<GenerateQuotePdfResult> {
  // 1. Fetch full quote with sections + lines + joins.
  const { data: quote, error: qErr } = await (supabase as any)   // mig 455 columns are newer than the generated types
    .from("quotes")
    .select(`
      quote_id, quote_number, quote_date, valid_until, revision_number, status,
      scope_bullets_text, assumptions_exclusions_text, notes_text, terms_text,
      subtotal_excl_gst, gst_amount, total_incl_gst, pdf_version, is_simplified_pdf,
      site_name_text, site_address_text, client_reference, client_contact_text, client_name_text, pricing_basis,
      client:clients (*),
      site:sites (*),
      bid:bids (*),
      quote_sections (
        section_id, section_name, section_description, sort_order,
        quote_lines (
          line_number, sort_order, description, description_secondary, quantity, unit,
          cost_price_ex_gst, sell_price_ex_gst, line_total_ex_gst,
          qty_people, qty_hours, qty_days,
          rate_line:rate_lines (line_code, display_name)
        )
      )
    `)
    .eq("quote_id", quoteId)
    .single();

  if (qErr || !quote) {
    throw new Error(`Could not load quote: ${qErr?.message ?? "not found"}`);
  }

  // mig 454/455 — a schedule-of-rates quote has no Coretel sections: its lines
  // sit flat, grouped by the client's own headings, with PSUM items on top.
  // Shape them as sections so the client gets the same Coretel PDF (James,
  // 27 Oct: "it needs a PDF quote to be sent to the client same as a Fixed
  // price"). Each client section → a section; each PSUM item → a section
  // whose lines carry the bucket; the "explain qty claimed" comment prints
  // under the item as its detail line. Cost and margin never print.
  if ((quote as any).pricing_basis === "schedule_of_rates") {
    const sel: SorPdfSelection = selection ?? { includeSor: true, psumItemIds: null };
    (quote as any).quote_sections = await sorLinesAsSections(quoteId, sel);
    // a partial PDF (a batch of PSUMs, say) must total what it shows, not the whole quote
    const sub = ((quote as any).quote_sections as QuoteSectionRow[]).reduce((a, sec) => a + sec.quote_lines.reduce((b, l) => b + Number(l.line_total_ex_gst ?? 0), 0), 0);
    const subR = Math.round(sub * 100) / 100, gst = Math.round(subR * 10) / 100;
    (quote as any).subtotal_excl_gst = subR; (quote as any).gst_amount = gst; (quote as any).total_incl_gst = Math.round((subR + gst) * 100) / 100;
  }

  // 2. Load the Coretel logo (don't block on failure — PDF still renders without it).
  const logoDataUrl = await fetchLogoDataUrl("/coretel-logo-full.png");
  console.log("[quotePdf] Logo loaded:", logoDataUrl ? `${logoDataUrl.length} chars` : "null");

  // 3. Build pdfmake document definition.
  console.log("[quotePdf] Building doc definition...");
  const docDef = buildDocDefinition(quote as unknown as QuoteRow, {
    logoDataUrl,
    preparedByName:  preparedBy.name,
    preparedByEmail: preparedBy.email,
  });
  console.log("[quotePdf] Doc def built. Content blocks:", docDef.content?.length,
    " Sections:", (quote as any).quote_sections?.length);

  // 4. Load pdfmake lazily + render to blob.
  console.log("[quotePdf] Loading pdfmake...");
  const pdfMake = await getPdfMake();
  console.log("[quotePdf] pdfmake loaded, building doc...");

  const blob: Blob = await new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("PDF render timed out after 20s — likely invalid doc definition. Check console for [quotePdf] logs."));
    }, 20000);

    try {
      const pdfDoc = pdfMake.createPdf(docDef);
      console.log("[quotePdf] createPdf succeeded, calling getBlob...");
      pdfDoc.getBlob(
        (b: Blob) => {
          clearTimeout(timeoutId);
          console.log("[quotePdf] getBlob returned, size =", b?.size);
          if (!b) {
            reject(new Error("pdfmake returned empty blob"));
            return;
          }
          resolve(b);
        },
        // pdfmake's getBlob also accepts an options arg; wrapping in try/catch
        // inside the callback-style API catches most synchronous render errors.
      );
    } catch (e: any) {
      clearTimeout(timeoutId);
      console.error("[quotePdf] createPdf/getBlob threw:", e);
      reject(new Error(`pdfmake render failed: ${e?.message ?? e}`));
    }
  });
  console.log("[quotePdf] blob ready, uploading to storage...");

  // 5. Compute next version + path.
  const nextVersion = (quote.pdf_version ?? 0) + 1;
  // T-244 — include the R-suffix in the filename so revisions don't share names on disk.
  const safeNumber = String(formatQuoteNumber(quote)).replace(/[^A-Za-z0-9_-]/g, "-");
  const storagePath = `${quote.quote_id}/${safeNumber}_v${nextVersion}.pdf`;

  // 6. Upload to storage.
  const { error: upErr } = await supabase.storage
    .from("quote-pdfs")
    .upload(storagePath, blob, {
      contentType: "application/pdf",
      upsert: true,
      cacheControl: "3600",
    });

  if (upErr) {
    throw new Error(`PDF upload failed: ${upErr.message}`);
  }

  // 7. Update quote row.
  const { data: authData } = await supabase.auth.getUser();
  const { error: updErr } = await supabase
    .from("quotes")
    .update({
      pdf_storage_path: storagePath,
      pdf_version:      nextVersion,
      pdf_generated_at: new Date().toISOString(),
      pdf_generated_by: authData?.user?.id ?? null,
    })
    .eq("quote_id", quoteId);

  if (updErr) {
    // Not fatal — the file is already in storage, just log
    console.warn("Failed to update pdf metadata on quote:", updErr.message);
  }

  // 8. Generate a signed URL (1hr).
  const { data: signed, error: sErr } = await supabase.storage
    .from("quote-pdfs")
    .createSignedUrl(storagePath, 3600);

  if (sErr || !signed) {
    throw new Error(`Could not generate signed URL: ${sErr?.message ?? "unknown"}`);
  }

  return { signedUrl: signed.signedUrl, storagePath, version: nextVersion };
}

/**
 * Gets a signed URL for the latest PDF of a quote (if any).
 * Returns null if no PDF has been generated yet.
 */
async function sorLinesAsSections(quoteId: string, sel: SorPdfSelection): Promise<QuoteSectionRow[]> {
  const { data: lines, error } = await (supabase as any).from("quote_lines")
    .select("quote_line_id, line_number, sort_order, description, description_secondary, quantity, unit, cost_price_ex_gst, sell_price_ex_gst, line_total_ex_gst, qty_people, qty_hours, qty_days, client_comment, psum_item_id, psum_bucket, rate_line:rate_lines (line_code, display_name, client_section)")
    .eq("quote_id", quoteId).order("sort_order").order("line_number");
  if (error) throw new Error(`Could not load the SoR lines: ${error.message}`);
  const { data: items } = await (supabase as any).from("quote_psum_items")
    .select("psum_item_id, title, sort_order, client_comment, source").eq("quote_id", quoteId).order("sort_order");

  const asRow = (l: any, detail: string | null): QuoteLineRow => ({
    line_number: l.line_number, sort_order: l.sort_order,
    description: l.description, description_secondary: detail,
    quantity: Number(l.quantity ?? 0), unit: l.unit ?? "each",
    cost_price_ex_gst: null,                       // never printed
    sell_price_ex_gst: Number(l.sell_price_ex_gst ?? 0), line_total_ex_gst: Number(l.line_total_ex_gst ?? 0),
    qty_people: null, qty_hours: null, qty_days: null,
    rate_line: l.rate_line ? { line_code: l.rate_line.line_code ?? null, display_name: l.rate_line.display_name ?? null } : null,
  });

  const out: QuoteSectionRow[] = [];
  let order = 0;
  // 1 · the schedule of rates, grouped by the client's headings, qty > 0 only
  const sor = sel.includeSor ? (lines ?? []).filter((l: any) => !l.psum_item_id && Number(l.quantity) > 0) : [];
  const groups = new Map<string, any[]>();
  for (const l of sor) { const k = l.rate_line?.client_section?.trim() || "Schedule of rates"; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(l); }
  for (const [name, ls] of groups) {
    out.push({ section_id: `sor:${name}`, section_name: name === "Schedule of rates" ? "Schedule of rates" : `Schedule of rates · ${name}`, section_description: null, sort_order: ++order,
      quote_lines: ls.map((l: any) => asRow(l, l.client_comment?.trim() ? l.client_comment.trim() : (l.description_secondary ?? null))) });
  }
  // 2 · each Non-SoR / PSUM item is its own section, named by the item alone
  //     (James, 27 Oct: no "Non-SoR item —" stamped on every one), with its
  //     labour / plant / material / other lines inside and the comment to the
  //     client under the first line.
  for (const it of (items ?? [])) {
    if (sel.psumItemIds && !sel.psumItemIds.includes(it.psum_item_id)) continue;
    const ls = (lines ?? []).filter((l: any) => l.psum_item_id === it.psum_item_id && Number(l.quantity) > 0 && Number(l.sell_price_ex_gst) > 0);
    if (!ls.length) continue;
    out.push({ section_id: it.psum_item_id, section_name: it.title, section_description: it.client_comment ?? null, sort_order: ++order,
      // the typed description IS the line — no bucket word in front of it
      quote_lines: ls.map((l: any, i: number) => asRow(l, i === 0 && it.client_comment?.trim() ? it.client_comment.trim() : null)) });
  }
  return out;
}

export async function getLatestQuotePdfUrl(quoteId: string): Promise<string | null> {
  const { data: quote } = await supabase
    .from("quotes")
    .select("pdf_storage_path")
    .eq("quote_id", quoteId)
    .single();

  if (!quote?.pdf_storage_path) return null;

  const { data: signed } = await supabase.storage
    .from("quote-pdfs")
    .createSignedUrl(quote.pdf_storage_path, 3600);

  return signed?.signedUrl ?? null;
}
// -----------------------------------------------------------------------------
// Shared with componentStatementPdf.ts (client-evidence component statements,
// 28 Aug 2026) — same engine, same brand tokens, one look across documents.
// -----------------------------------------------------------------------------
export { getPdfMake, fetchLogoDataUrl, COLOR as PDF_COLOR, COMPANY as PDF_COMPANY };
